import toQueryParams from 'jquery-param'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { trackingEvents } from 'modules/core/lib/Tracking'
import type { Return } from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type ReportStruct from 'modules/reports/lib/ReportStruct'
import type { Variable } from 'modules/reports/store/models/Report'
import type Report from 'modules/reports/store/models/Report'

import { useReportTracking } from './useReportTracking'

type Format = 'xlsx' | 'csv' | 'txt'

const exportReport = async (
  format: Format,
  reportStruct: ReportStruct,
  variables: Variable[],
  delimiter?: string
) => {
  try {
    app.setProgress({
      message: i18n.t('custom_reports.download_loading'),
    })

    const response = await reportStruct.order(variables)
    const report = reportStruct.props.resource
    const reportId = report.has('id') && report.attributes.id

    const query = toQueryParams({
      token: response.token,
      id: reportId || null,
      export_format: format,
      ...(delimiter !== undefined ? { delimiter } : {}),
    })

    const url = buildUrl(`/reports/reports/download?${query}`)
    window.location.href = url
  } catch (exception) {
    // eslint-disable-next-line no-console
    console.error(exception)
    app.setFlash({
      type: 'wrong',
      message: i18n.t('errors.generic_requesting'),
    })
  } finally {
    app.setProgress(null)
  }
}

interface HookProps {
  reportStruct: ReportStruct<Report>
  variables: Variable[]
}

interface HookReturnType {
  onSubmit: () => Promise<Return<void>>
}

const useReportExportCsv = ({ reportStruct, variables }: HookProps): HookReturnType => {
  const { track } = useReportTracking({ reportStruct })
  const { performAction: onSubmit } = useAsyncAction(
    async () => {
      await exportReport('csv', reportStruct, variables)

      track(trackingEvents.reports.downloadReportSuccess)
    },
    {
      successFlash: false,
    }
  )

  return {
    onSubmit,
  }
}

const useReportExportExcel = ({ reportStruct, variables }: HookProps): HookReturnType => {
  const { track } = useReportTracking({ reportStruct })
  const { performAction: onSubmit } = useAsyncAction(
    async () => {
      await exportReport('xlsx', reportStruct, variables)

      track(trackingEvents.reports.downloadReportSuccess)
    },
    {
      successFlash: false,
    }
  )

  return {
    onSubmit,
  }
}

const useReportExportTxt = ({ reportStruct, variables }: HookProps): HookReturnType => {
  const { track } = useReportTracking({ reportStruct })
  const { performAction: onSubmit } = useAsyncAction(
    async () => {
      await exportReport('txt', reportStruct, variables)

      track(trackingEvents.reports.downloadReportSuccess)
    },
    {
      successFlash: false,
    }
  )

  return {
    onSubmit,
  }
}

const useReportExportDsv = ({ reportStruct, variables }: HookProps): HookReturnType => {
  const { track } = useReportTracking({ reportStruct })
  const { performAction: onSubmit } = useAsyncAction(
    async () => {
      await exportReport('txt', reportStruct, variables, '|')

      track(trackingEvents.reports.downloadReportSuccess)
    },
    {
      successFlash: false,
    }
  )

  return {
    onSubmit,
  }
}

export { useReportExportCsv, useReportExportExcel, useReportExportTxt, useReportExportDsv }
